import { CSSProperties, useCallback } from "react";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import IconsOrTagline from "../IconsOrTagline";
import SelectBox from "../SelectBox";
import { algoliaProductSearch } from "../../services/algoliaService";
import { Flags } from "../Flags";
import { useLang } from "../../hooks/LangHooks";
import { useUserProducts } from "../../hooks/ProductHooks";
import { useLocSettings } from "../../hooks/SettingsHooks";
import { Product } from "../../types/Product";

import "./style.scss";

const ProductSelectItem = ({ item }: { item: Partial<Product> }) => {
  const { name, tagline, images, specsIndexed, sku } = item;
  return (
    <>
      <div className={"image-col"}>
        <div className="image-container">
          <div
            className="productPreviewImage"
            style={{
              backgroundImage: `url("${images?.[0].styles?.preview || ""}")`
            }}
          ></div>
        </div>
      </div>
      <div className={"info-col"}>
        <h4>
          <span className="text-pale">{sku} </span>
          <span>{name}</span>
          <Flags product={item as Product} inline={true} />
        </h4>
        <div className="specs">
          <IconsOrTagline
            specsIndexed={specsIndexed}
            showLabel={false}
            sku={sku}
            tagline={tagline}
            noTooltips
          />
        </div>
      </div>
    </>
  );
};

const ProductSelectedValueDisplay = ({ item }: { item: Partial<Product> }) => {
  const firstImage = item?.images?.[0].styles?.preview;
  return (
    <>
      <div
        className="selected-value-display-image"
        style={
          firstImage
            ? {
                backgroundImage: `url("${firstImage}")`
              }
            : {}
        }
      ></div>
      <div className="selected-value-display-text">
        <span className="text-pale">{item.sku}</span>
        <span className={"ms-2"}>{item.name}</span>
        <Flags product={item as Product} inline={true} />
      </div>
    </>
  );
};

const ProductSelect = ({
  initialValue,
  onChange,
  allowCustomInput = false,
  id = "",
  className = "product-select",
  itemClassName = "product-select-item",
  style
}: {
  initialValue: object;
  onChange?: (_item: object | null) => void;
  allowCustomInput?: boolean;
  id?: string;
  className?: string;
  itemClassName?: string;
  style?: CSSProperties;
}) => {
  const lang = useLang();
  const activeProducts = useUserProducts();
  const settings = useLocSettings();

  const productSearch = useCallback(
    async (query: string, limit = 8) => {
      const { products } = await algoliaProductSearch(
        query,
        limit,
        lang,
        activeProducts,
        settings
      );
      //console.log("productSearch", products);
      return products;
    },
    [activeProducts, lang, settings]
  );

  const t = useBjelinTranslations();
  return (
    <SelectBox
      ItemComponent={ProductSelectItem}
      SelectedValueDisplay={ProductSelectedValueDisplay}
      itemIdField="sku"
      itemTitleField="name"
      searchFn={productSearch}
      initialValue={initialValue}
      onChange={onChange}
      placeholder={t("search.field-label")}
      hintText={t("search.hint")}
      noMatchText={t("search.b2b.no-match")}
      allowCustomInput={allowCustomInput}
      id={id}
      className={className}
      itemClassName={itemClassName}
      style={style}
    />
  );
};

export default ProductSelect;
